import React, { useState, useEffect, useCallback } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import type { Form } from '@lds-efsp/shared-contracts';
import {
  SetupDetailDrawer,
  SetupFilterBar,
  SetupDataTable,
  StatusBadge,
  SetupItemPreviewPopover,
} from '../components';
import FormInput, { FormSelect, FormTextarea, FormCheckbox } from '../components/FormInput';
import { useApi } from '../../../api/useApi';

interface SetupFormsProps {
  brand: BrandConfig;
  Icons: IconsType;
}

const formTypeOptions = [
  { value: 'proof-of-service', label: 'Proof of Service' },
  { value: 'affidavit', label: 'Affidavit' },
  { value: 'declaration', label: 'Declaration' },
  { value: 'notice', label: 'Notice' },
  { value: 'cover-sheet', label: 'Cover Sheet' },
  { value: 'email-template', label: 'Email Template' },
  { value: 'letter-template', label: 'Letter Template' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'other', label: 'Other' },
];

const courtTypeOptions = [
  { value: 'all', label: 'All Courts' },
  { value: 'superior', label: 'Superior' },
  { value: 'federal', label: 'Federal' },
  { value: 'bankruptcy', label: 'Bankruptcy' },
  { value: 'appellate', label: 'Appellate' },
  { value: 'small-claims', label: 'Small Claims' },
  { value: 'family', label: 'Family' },
  { value: 'probate', label: 'Probate' },
];

const templateTypeOptions = [
  { value: 'pdf-form', label: 'PDF Form' },
  { value: 'rich-text', label: 'Rich Text Template' },
];

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'draft', label: 'Draft' },
  { value: 'archived', label: 'Archived' },
];

const mannerOfServiceOptions = [
  { value: '', label: 'N/A' },
  { value: 'personal', label: 'Personal' },
  { value: 'substituted', label: 'Substituted' },
  { value: 'posting', label: 'Posting' },
  { value: 'mailing', label: 'Mailing' },
  { value: 'certified-mail', label: 'Certified Mail' },
  { value: 'electronic', label: 'Electronic' },
  { value: 'publication', label: 'Publication' },
  { value: 'diligence', label: 'Diligence' },
];

const SetupForms: React.FC<SetupFormsProps> = ({ brand, Icons }) => {
  const { get, post, patch } = useApi();
  const [forms, setForms] = useState<Form[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedForm, setSelectedForm] = useState<Form | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<Partial<Form>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'template'>('details');

  const fetchForms = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (categoryFilter !== 'all') params.set('category', categoryFilter);

      const data = await get<{ data: Form[] }>(`/api/admin/setup/forms?${params}`);
      setForms(data.data || []);
    } catch (error) {
      console.error('Failed to fetch forms:', error);
    } finally {
      setIsLoading(false);
    }
  }, [get, search, statusFilter, categoryFilter]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    fetchForms();
  }, [fetchForms]);

  const handleRowClick = (form: Form) => {
    setSelectedForm(form);
    setFormData(form);
    setIsCreating(false);
    setActiveTab('details');
    setIsDrawerOpen(true);
  };

  const handleAdd = () => {
    setSelectedForm(null);
    setFormData({
      code: '',
      name: '',
      formType: 'proof-of-service',
      courtType: 'all',
      templateType: 'pdf-form',
      status: 'draft',
      automated: false,
      vendorForm: false,
      notaryRequired: false,
      signatureRequired: true,
      availableVariables: [],
      fieldMappings: [],
    });
    setIsCreating(true);
    setActiveTab('details');
    setIsDrawerOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (isCreating) {
        await post('/api/admin/setup/forms', formData);
      } else {
        await patch(`/api/admin/setup/forms/${selectedForm?.id}`, formData);
      }

      setIsDrawerOpen(false);
      fetchForms();
    } catch (error) {
      console.error('Failed to save form:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const columns = [
    {
      key: 'code',
      header: 'Code',
      width: '140px',
      render: (form: Form) => (
        <div className="flex items-center gap-2">
          <span className="font-medium" style={{ color: brand.colors.link }}>
            {form.code}
          </span>
          <SetupItemPreviewPopover
            brand={brand}
            title={form.name}
            fields={[
              { label: 'Code', value: form.code },
              { label: 'Type', value: form.formType.replace(/-/g, ' ') },
              { label: 'Court', value: form.courtType },
              { label: 'Template', value: form.templateType === 'pdf-form' ? 'PDF' : 'Rich Text' },
              { label: 'Uses', value: String(form.usageCount || 0) },
            ]}
          />
        </div>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      render: (form: Form) => (
        <div>
          <div className="font-medium" style={{ color: brand.colors.text }}>
            {form.name}
          </div>
          {form.description && (
            <div className="text-xs" style={{ color: brand.colors.textLight }}>
              {form.description}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'formType',
      header: 'Type',
      render: (form: Form) => (
        <span className="capitalize">{form.formType.replace(/-/g, ' ')}</span>
      ),
    },
    {
      key: 'templateType',
      header: 'Template',
      render: (form: Form) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
            form.templateType === 'pdf-form' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
          }`}
        >
          {form.templateType === 'pdf-form' ? 'PDF' : 'Rich Text'}
        </span>
      ),
    },
    {
      key: 'automated',
      header: 'Auto',
      render: (form: Form) => (
        <span>{form.automated ? 'Yes' : 'No'}</span>
      ),
    },
    {
      key: 'usageCount',
      header: 'Usage',
      render: (form: Form) => (
        <span>{form.usageCount.toLocaleString()}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (form: Form) => <StatusBadge status={form.status} />,
    },
  ];

  const tabs = [
    { key: 'details' as const, label: 'Form Details' },
    { key: 'template' as const, label: 'Template Content' },
  ];

  return (
    <div>
      <SetupFilterBar
        brand={brand}
        Icons={Icons}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search forms..."
        statusFilter={statusFilter}
        statusOptions={statusOptions}
        onStatusChange={setStatusFilter}
        categoryFilter={categoryFilter}
        categoryOptions={formTypeOptions}
        onCategoryChange={setCategoryFilter}
        onAdd={handleAdd}
        addLabel="Add Form"
      />

      <SetupDataTable
        brand={brand}
        Icons={Icons}
        columns={columns}
        data={forms}
        isLoading={isLoading}
        onRowClick={handleRowClick}
        emptyMessage="No forms found"
        getRowId={(form) => form.id}
      />

      <SetupDetailDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={isCreating ? 'Add Form' : 'Edit Form'}
        subtitle={selectedForm?.code}
        brand={brand}
        Icons={Icons}
        width="xl"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsDrawerOpen(false)}
              className="px-4 py-2 border rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
              style={{ borderColor: brand.colors.border, color: brand.colors.text }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      >
        {/* Tabs */}
        {!isCreating && formData.templateType === 'rich-text' && (
          <div
            className="flex border-b mb-6 -mx-6 px-6"
            style={{ borderColor: brand.colors.border }}
          >
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === tab.key ? '' : 'border-transparent'
                }`}
                style={{
                  color: activeTab === tab.key ? brand.colors.link : brand.colors.textLight,
                  borderColor: activeTab === tab.key ? brand.colors.primary : 'transparent',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Details Tab */}
        {(activeTab === 'details' || isCreating || formData.templateType !== 'rich-text') && (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <FormInput
                brand={brand}
                label="Code"
                name="code"
                value={formData.code || ''}
                onChange={handleInputChange}
                required
                placeholder="e.g., SC-104"
              />
              <FormSelect
                brand={brand}
                label="Form Type"
                name="formType"
                value={formData.formType || 'proof-of-service'}
                onChange={handleInputChange}
                options={formTypeOptions}
              />
              <FormSelect
                brand={brand}
                label="Template Type"
                name="templateType"
                value={formData.templateType || 'pdf-form'}
                onChange={handleInputChange}
                options={templateTypeOptions}
              />
            </div>

            <FormInput
              brand={brand}
              label="Name"
              name="name"
              value={formData.name || ''}
              onChange={handleInputChange}
              required
            />

            <FormTextarea
              brand={brand}
              label="Description"
              name="description"
              value={formData.description || ''}
              onChange={handleInputChange}
              rows={2}
            />

            <div className="grid grid-cols-3 gap-4">
              <FormSelect
                brand={brand}
                label="Court Type"
                name="courtType"
                value={formData.courtType || 'all'}
                onChange={handleInputChange}
                options={courtTypeOptions}
              />
              <FormInput
                brand={brand}
                label="State"
                name="state"
                value={formData.state || ''}
                onChange={handleInputChange}
                placeholder="e.g., CA"
              />
              <FormSelect
                brand={brand}
                label="Manner of Service"
                name="mannerOfService"
                value={formData.mannerOfService || ''}
                onChange={handleInputChange}
                options={mannerOfServiceOptions}
              />
            </div>

            <div
              className="border-t pt-4"
              style={{ borderColor: brand.colors.border }}
            >
              <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
                Requirements
              </h3>
              <div className="grid grid-cols-2 gap-2">
                <FormCheckbox
                  brand={brand}
                  label="Signature Required"
                  name="signatureRequired"
                  checked={formData.signatureRequired || false}
                  onChange={handleInputChange}
                />
                <FormCheckbox
                  brand={brand}
                  label="Notary Required"
                  name="notaryRequired"
                  checked={formData.notaryRequired || false}
                  onChange={handleInputChange}
                />
                <FormCheckbox
                  brand={brand}
                  label="Witness Required"
                  name="witnessRequired"
                  checked={formData.witnessRequired || false}
                  onChange={handleInputChange}
                />
                <FormCheckbox
                  brand={brand}
                  label="Automated Generation"
                  name="automated"
                  checked={formData.automated || false}
                  onChange={handleInputChange}
                />
                <FormCheckbox
                  brand={brand}
                  label="Vendor Form"
                  name="vendorForm"
                  checked={formData.vendorForm || false}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {formData.templateType === 'pdf-form' && (
              <div
                className="border-t pt-4"
                style={{ borderColor: brand.colors.border }}
              >
                <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
                  PDF Template
                </h3>
                <FormInput
                  brand={brand}
                  label="PDF Template URL"
                  name="pdfTemplateUrl"
                  type="url"
                  value={formData.pdfTemplateUrl || ''}
                  onChange={handleInputChange}
                  placeholder="https://..."
                />
                <FormInput
                  brand={brand}
                  label="PDF Filename"
                  name="pdfFileName"
                  value={formData.pdfFileName || ''}
                  onChange={handleInputChange}
                  placeholder="e.g., sc-104.pdf"
                />
                {formData.fieldMappings && formData.fieldMappings.length > 0 && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium mb-2" style={{ color: brand.colors.text }}>
                      Field Mappings ({formData.fieldMappings.length})
                    </label>
                    <div
                      className="p-3 rounded-lg text-sm"
                      style={{ backgroundColor: brand.colors.bgLight, color: brand.colors.textLight }}
                    >
                      {formData.fieldMappings.map(m => m.fieldName).join(', ')}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div
              className="border-t pt-4"
              style={{ borderColor: brand.colors.border }}
            >
              <FormSelect
                brand={brand}
                label="Status"
                name="status"
                value={formData.status || 'draft'}
                onChange={handleInputChange}
                options={statusOptions}
              />
              <FormTextarea
                brand={brand}
                label="Instructions"
                name="instructions"
                value={formData.instructions || ''}
                onChange={handleInputChange}
                rows={2}
                helpText="Instructions displayed to users when completing this form"
              />
              <FormTextarea
                brand={brand}
                label="Notes"
                name="notes"
                value={formData.notes || ''}
                onChange={handleInputChange}
                rows={2}
              />
            </div>
          </div>
        )}

        {/* Template Content Tab (for rich-text forms) */}
        {activeTab === 'template' && formData.templateType === 'rich-text' && !isCreating && (
          <div className="space-y-6">
            <div>
              <span className="block text-sm font-medium mb-2" style={{ color: brand.colors.text }}>
                Template Content
              </span>
              <div
                className="p-4 border rounded-lg min-h-[300px] text-sm font-mono whitespace-pre-wrap"
                style={{
                  borderColor: brand.colors.border,
                  backgroundColor: brand.colors.bgLight,
                  color: brand.colors.text,
                }}
              >
                {formData.templateContent || 'No template content defined.'}
              </div>
              <p className="mt-2 text-xs" style={{ color: brand.colors.textLight }}>
                Rich text editor integration coming soon. Template variables: {formData.availableVariables?.join(', ') || 'None defined'}
              </p>
            </div>

            <div>
              <FormTextarea
                brand={brand}
                label="Edit Template (Raw HTML)"
                name="templateContent"
                value={formData.templateContent || ''}
                onChange={handleInputChange}
                rows={10}
                helpText="Enter template content with merge variables like %OrderNumber%, %ContactName%, etc."
              />
            </div>
          </div>
        )}
      </SetupDetailDrawer>
    </div>
  );
};

export default SetupForms;
